import Modal from "components/elements/Modal";
import StatusTrackerStep, { EStatusTrackerInfoStatus, EStatusTrackerPosition, EStatusTrackerStatus, IStatusTrackerInfo, IStepsConfig } from "./StatusTrackerStep";
import classes from "./classes.module.scss";
import Typography, { ITypo } from "components/elements/Typography";
import Button, { EButtonVariant } from "components/elements/Button";
import I18n from "components/materials/I18n";
import { ESubStep } from "common/enums/Subscription/ESubStep";
import * as P from "ts-pattern";
import { UserContext } from "contexts/User";
import { useCallback, useContext, useEffect, useState } from "react";
import { container } from "tsyringe";
import ProductSubscribedResponseResource from "common/resources/ProductSubscribed/ProductSubscribedResponseResource";
import SubscriptionSignatureLightResponseResource from "common/resources/Subscription/Signature/SubscriptionSignatureLightResponseResource";
import { ESubscriptionSignatureType } from "common/enums/Signature/ESubscriptionSignatureType";
import { ESignatureSignerStatus } from "common/enums/Signature/ESignatureSignerStatus";
import SubscriptionResponseResource from "common/resources/Subscription/SubscriptionResponseResource";
import ClientSheetLightResponseResource from "common/resources/ClientSheet/ClientSheetLightResponseResource";
import { ESubscriptionType } from "common/enums/Subscription/ESubscriptionType";
import SubscriptionService from "services/SubscriptionService";
import SubscriptionSignatureSignerResponseResource from "common/resources/Subscription/Signature/SubscriptionSignatureSignerResponseResource";
import { ESignatureStatus } from "common/enums/Signature/ESignatureStatus";
import ProductSubscribedService from "services/ProductSubscribedService";
import SubscriptionSignatureService from "services/SubscriptionSignatureService";
import Snackbar from "@mui/material/Snackbar";
import { Alert } from "@mui/material";
import SubscriptionSignatureCommunitySpouseSignerResponseResource from "common/resources/Subscription/Signature/SubscriptionSignatureCommunitySpouseSignerResponseResource";
import { isPrivateEquityOnly } from "common/utils/privateEquity";
import { getSubscriptionTrackerSteps, SubscriptionTrackerStepStatus } from "common/utils/subscriptionTracker";
import ProductSubscribedTransferModeResponseResource from "common/resources/ProductSubscribed/ProductSubscribedTransferModeResponseResource";
import ProductSubscribedCounterSignatureResponseResource from "common/resources/ProductSubscribed/ProductSubscribedCounterSignatureResponseResource";
import ProductSubscribedTransferDossierRequestResource from "common/resources/ProductSubscribed/ProductSubscribedTransferDossierRequestResource";
import useOpenable from "hooks/useOpenable";
import TransferDossierModal from "../TransferDossierModal";

const subscriptionService = container.resolve(SubscriptionService);
const productSubscribedService = container.resolve(ProductSubscribedService);
const subscriptionSignatureService = container.resolve(SubscriptionSignatureService);

interface ISteps {
	signatureRto: EStatusTrackerStatus;
	signatureSubscription: EStatusTrackerStatus;
	sendSignedDocuments: EStatusTrackerStatus;
	validation: EStatusTrackerStatus;
}

const STEP_STATUS: Record<SubscriptionTrackerStepStatus, EStatusTrackerStatus> = {
	inactive: EStatusTrackerStatus.INACTIVE,
	active: EStatusTrackerStatus.ACTIVE,
	done: EStatusTrackerStatus.DONE,
};

type IProps = {
	isOpen: boolean;
	onClose: () => void;
	signatureInfo: SubscriptionSignatureLightResponseResource[];
	productSubscribed: ProductSubscribedResponseResource | null;
	subscription: SubscriptionResponseResource | null;
	coSubscriber: ClientSheetLightResponseResource | null;
	/** Private equity : façon de transmettre le dossier et demande adressée à la société de gestion. Absent pour les autres produits. */
	transferMode?: ProductSubscribedTransferModeResponseResource | null;
	onDossierTransferred?: () => void;
};

export default function StatusTrackerModal(props: IProps) {
	const { user } = useContext(UserContext);
	const [hasSendSignedDocuments, setHasSendSignedDocuments] = useState(false);
	const [hasValidateSubscription, setHasValidateSubscription] = useState(false);
	const [isResendDocumentsModalOpen, setIsResendDocumentsModalOpen] = useState(false);
	const [openSuccessTransferAlert, setOpenSuccessTransferAlert] = useState(false);
	const [isTransferDocumentsLoading, setIsTransferDocumentsLoading] = useState(false);
	const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [transferredTo, setTransferredTo] = useState<string | null>(null);
	const transferDossierModal = useOpenable();
	const [signatureAction, setSignatureAction] = useState<"transfer" | "prepare">("transfer");
	const openSignatureAction = (action: "transfer" | "prepare") => {
		setSignatureAction(action);
		transferDossierModal.open();
	};
	const [stepStatus, setStepStatus] = useState<ISteps>({
		signatureRto: EStatusTrackerStatus.INACTIVE,
		signatureSubscription: EStatusTrackerStatus.INACTIVE,
		sendSignedDocuments: EStatusTrackerStatus.INACTIVE,
		validation: EStatusTrackerStatus.INACTIVE,
	});
	const isUserCGP = user?.role?.name === "CGP";
	// Une souscription de private equity n'a pas de convention RTO : son suivi commence à la signature du dossier.
	const withoutRto = isPrivateEquityOnly(props.subscription?.productsSubscribed);
	// Private equity dont la société de gestion signe le contrat : la transmission lui demande sa signature, qui valide le dossier.
	const counterSignature = props.transferMode?.counterSignature === true;
	const counterSignatureRequest = props.transferMode?.request ?? null;
	// Dossier transmis sans demande en cours auprès de la société de gestion (étape posée à la main, par exemple) : la demande reste à faire.
	const isCounterSignatureMissing = counterSignature && counterSignatureRequest === null && props.productSubscribed?.step === ESubStep.waiting_validation;
	// Tant que la société de gestion n'a pas signé, la demande peut repartir, vers elle ou vers un autre signataire.
	const canResendCounterSignature =
		counterSignatureRequest !== null &&
		(!counterSignatureRequest.sequential || !!counterSignatureRequest.releasedAt) &&
		counterSignatureRequest.status !== ESignatureStatus.done &&
		props.productSubscribed?.step !== ESubStep.validated;

	const toggleIsTransferDocumentsLoading = useCallback(() => setIsTransferDocumentsLoading((prev) => !prev), []);
	const toggleResendDocumentsModal = useCallback(() => setIsResendDocumentsModalOpen((prev) => !prev), []);

	// L'avancement est calculé par `getSubscriptionTrackerSteps` (common), couvert par les tests de non-régression du tunnel.
	const getStepStatus = useCallback((): ISteps => {
		const steps = getSubscriptionTrackerSteps(props.signatureInfo, props.productSubscribed?.step, withoutRto);
		return {
			signatureRto: STEP_STATUS[steps.signatureRto],
			signatureSubscription: STEP_STATUS[steps.signatureSubscription],
			sendSignedDocuments: STEP_STATUS[steps.sendSignedDocuments],
			validation: STEP_STATUS[steps.validation],
		};
	}, [props.signatureInfo, props.productSubscribed?.step, withoutRto]);

	const toggleSuccessTransferAlert = useCallback(() => setOpenSuccessTransferAlert((prev) => !prev), []);
	const closeSuccessAlert = useCallback(() => setOpenSuccessAlert(false), []);

	const resendDocuments = useCallback(() => {
		const subscriptionId = props.productSubscribed?.subscriptionId;
		if (!subscriptionId) return;
		const isRtoStepActive = stepStatus.signatureRto === EStatusTrackerStatus.ACTIVE;
		const isSubscriptionStepActive = stepStatus.signatureSubscription === EStatusTrackerStatus.ACTIVE;
		if (!isRtoStepActive && !isSubscriptionStepActive) {
			if (isResendDocumentsModalOpen) toggleResendDocumentsModal();
			return;
		}
		setIsLoading(true);
		subscriptionSignatureService
			.resendSignature(subscriptionId)
			.then(() => {
				if (isResendDocumentsModalOpen) toggleResendDocumentsModal();
				setOpenSuccessAlert(true);
				setIsLoading(false);
			})
			.catch((error) => console.error(error));
	}, [stepStatus, toggleResendDocumentsModal, isResendDocumentsModalOpen, props.productSubscribed]);

	function getFormattedDate(date?: Date | null) {
		if (!date) return;
		// Date from API is a string in the format "2022-02-02T00:00:00.000Z"
		const newDate = new Date(date);
		return newDate.toLocaleDateString("fr-FR", {
			year: "2-digit",
			month: "2-digit",
			day: "2-digit",
		});
	}

	function getMockedLegalPersonSigners(): IStatusTrackerInfo[] {
		const corporationSubscriber = props.productSubscribed?.corporationSubscriber;
		if (!corporationSubscriber) return [];
		return [
			{
				text: I18n.trslt(I18n.asset.component.status_tracker_modal.waiting_signature, {
					name: `${corporationSubscriber.representantLegal?.firstName} ${corporationSubscriber.representantLegal?.lastName}`,
				}),
				status: EStatusTrackerInfoStatus.PENDING,
			},
		];
	}

	function getMockedNaturalPersonSigners(): IStatusTrackerInfo[] {
		const subscriber = props.productSubscribed?.subscriber;
		if (!subscriber) return [];

		const mockedSubscribers: IStatusTrackerInfo[] = [];
		mockedSubscribers.push({
			text: I18n.trslt(I18n.asset.component.status_tracker_modal.waiting_signature, {
				name: `${subscriber.firstName} ${subscriber.lastName}`,
			}),
			status: EStatusTrackerInfoStatus.PENDING,
		});

		const coSubscriber = props.coSubscriber;
		if (coSubscriber && props.subscription?.subscriptionType === ESubscriptionType.coSubscriptionNaturalPerson) {
			const mockedCoSubscriber: IStatusTrackerInfo = {
				text: I18n.trslt(I18n.asset.component.status_tracker_modal.waiting_signature, {
					name: `${coSubscriber.firstName} ${coSubscriber.lastName}`,
				}),
				status: EStatusTrackerInfoStatus.PENDING,
			};
			mockedSubscribers.push(mockedCoSubscriber);
		}

		const spouse = props.subscription?.communitySpouseDetails;
		if (
			spouse &&
			props.subscription?.subscriptionType === ESubscriptionType.naturalPerson &&
			props.signatureInfo.some((info) => info.communitySpouseSigners && info.communitySpouseSigners.length > 0)
		) {
			const mockedSpouse: IStatusTrackerInfo = {
				text: I18n.trslt(I18n.asset.component.status_tracker_modal.waiting_signature, {
					name: `${spouse.firstName} ${spouse.lastName}`,
				}),
				status: EStatusTrackerInfoStatus.PENDING,
			};
			mockedSubscribers.push(mockedSpouse);
		}

		return mockedSubscribers;
	}

	function getMockedSigners(): IStatusTrackerInfo[] {
		const subscriptionType = props.subscription?.subscriptionType;
		if (!subscriptionType) return [];

		return P.match<ESubscriptionType, IStatusTrackerInfo[]>(subscriptionType)
			.with(ESubscriptionType.coSubscriptionNaturalPerson, () => getMockedNaturalPersonSigners())
			.with(ESubscriptionType.naturalPerson, () => getMockedNaturalPersonSigners())
			.with(ESubscriptionType.legalPerson, () => getMockedLegalPersonSigners())
			.exhaustive();
	}

	function transferDocumentsToSg() {
		const productSubscribedId = props.productSubscribed?.id;
		if (!productSubscribedId) return;
		productSubscribedService
			.transferDocumentsToSg(productSubscribedId)
			.then(() => {
				toggleIsTransferDocumentsLoading();
				setOpenSuccessTransferAlert(true);
				setHasSendSignedDocuments(true);
			})
			.catch((error) => {
				console.error({ error });
				toggleIsTransferDocumentsLoading();
			});
	}

	function onDossierTransferred(signatory: ProductSubscribedTransferDossierRequestResource) {
		transferDossierModal.close();
		if (signatureAction === "transfer") {
			setTransferredTo(`${signatory.firstName} ${signatory.lastName}`);
			setHasSendSignedDocuments(true);
		} else {
			setHasSendSignedDocuments(false);
			setOpenSuccessAlert(true);
		}
		props.onDossierTransferred?.();
	}

	function validateSubscription() {
		const subscriptionId = props.productSubscribed?.subscriptionId;
		if (!subscriptionId) return;

		subscriptionService
			.validate(subscriptionId)
			.then(() => setHasValidateSubscription(true))
			.catch((error) => console.error({ error }));
	}

	function getSignatureInfoText(
		signer: SubscriptionSignatureSignerResponseResource | SubscriptionSignatureCommunitySpouseSignerResponseResource,
		hasSigned: boolean,
		hasDeclined: boolean,
		isSignatureExpired: boolean,
	) {
		let text = I18n.asset.component.status_tracker_modal.waiting_signature;
		if (hasSigned) {
			text = I18n.asset.component.status_tracker_modal.signed;
		} else if (hasDeclined) {
			text = I18n.asset.component.status_tracker_modal.declined;
		} else if (isSignatureExpired) {
			text = I18n.asset.component.status_tracker_modal.expired;
		}
		return I18n.trslt(text, { name: `${signer.signer?.firstName} ${signer.signer?.lastName}` });
	}

	function getSignatureInfoStatus(hasSigned: boolean, hasDeclined: boolean, isSignatureExpired: boolean) {
		if (hasSigned) return EStatusTrackerInfoStatus.DONE;
		if (hasDeclined || isSignatureExpired) return EStatusTrackerInfoStatus.ERROR;
		return EStatusTrackerInfoStatus.PENDING;
	}

	/**
	 * Retrieves the signature status information for a specific subscription type.
	 * If no matching signature information is found or no signers are available,
	 * mocked signers are returned
	 */
	function getSignatureInfo(type: ESubscriptionSignatureType): IStatusTrackerInfo[] {
		const signatureInfo =
			props.signatureInfo.find((info) => info.type === type) ??
			(type === ESubscriptionSignatureType.rto ? props.signatureInfo.find((info) => info.type === ESubscriptionSignatureType.subscription && info.includesRto) : undefined);
		if (!signatureInfo || signatureInfo.signers.length === 0) return getMockedSigners();
		const isSignatureExpired = signatureInfo.status === ESignatureStatus.expired;
		const signers = [...signatureInfo.signers, ...(signatureInfo.communitySpouseSigners || [])];
		return signers.map((signer) => {
			const { status, signedAt } = signer;
			const hasDeclined = status === ESignatureSignerStatus.declined;
			const hasSigned = status === ESignatureSignerStatus.signed;
			return {
				text: getSignatureInfoText(signer, hasSigned, hasDeclined, isSignatureExpired),
				date: getFormattedDate(signedAt),
				status: getSignatureInfoStatus(hasSigned, hasDeclined, isSignatureExpired),
			};
		});
	}

	function getSendSignedDocumentsInfo(): IStatusTrackerInfo[] {
		if (counterSignatureRequest?.sequential && !counterSignatureRequest.releasedAt && props.signatureInfo.some((i) => i.sequentialCounterSignature && i.investorSigned))
			return [{ button: { text: "Transmettre à IQ-EQ", onClick: () => openSignatureAction("transfer") } }];
		if (props.productSubscribed?.step === ESubStep.waiting_validation || props.productSubscribed?.step === ESubStep.validated || hasSendSignedDocuments) {
			return [
				{
					text: I18n.asset.component.status_tracker_modal.file_transmited,
					status: EStatusTrackerInfoStatus.DONE,
					date: getFormattedDate(props.productSubscribed?.sgTransmittedAt ?? counterSignatureRequest?.requestedAt),
				},
			];
		}
		// La transmission demande le signataire de la société de gestion ; le conseiller comme le middle office peuvent la faire.
		if (counterSignature) return [{ button: { text: I18n.asset.component.status_tracker_modal.transmit, onClick: () => openSignatureAction("transfer") } }];
		return [{ button: { text: I18n.asset.component.status_tracker_modal.transmit, onClick: transferDocumentsToSg, disabled: !isUserCGP || isTransferDocumentsLoading } }];
	}

	/** Où en est la signature de la personne qui signe pour la société de gestion. */
	function getCounterSignatureInfo(request: ProductSubscribedCounterSignatureResponseResource): IStatusTrackerInfo {
		const copy = I18n.asset.component.status_tracker_modal;
		const name = { name: `${request.firstName} ${request.lastName}` };
		if (request.status === ESignatureStatus.done || request.signerStatus === ESignatureSignerStatus.signed) {
			return { text: I18n.trslt(copy.signed, name), date: getFormattedDate(request.signedAt), status: EStatusTrackerInfoStatus.DONE };
		}
		if (request.status === ESignatureStatus.declined || request.signerStatus === ESignatureSignerStatus.declined) {
			return { text: I18n.trslt(copy.declined, name), status: EStatusTrackerInfoStatus.ERROR };
		}
		if (request.status === ESignatureStatus.expired) return { text: copy.expired, status: EStatusTrackerInfoStatus.ERROR };
		if (request.status === ESignatureStatus.canceled) return { text: copy.counter_signature.canceled, status: EStatusTrackerInfoStatus.ERROR };
		return { text: I18n.trslt(copy.waiting_signature, name), status: EStatusTrackerInfoStatus.PENDING };
	}

	function getValidationInfo(): IStatusTrackerInfo[] {
		// Une fois le dossier transmis, l'étape suit la signature de la société de gestion : elle vaut validation.
		if (counterSignature && counterSignatureRequest) {
			const signature = getCounterSignatureInfo(counterSignatureRequest);
			if (props.productSubscribed?.step !== ESubStep.validated) return [signature];
			const validatedAt = getFormattedDate(props.productSubscribed.validationDate);
			return [signature, { text: I18n.asset.component.status_tracker_modal.file_validated, status: EStatusTrackerInfoStatus.DONE, date: validatedAt }];
		}
		if (isCounterSignatureMissing) return [{ text: I18n.asset.component.status_tracker_modal.counter_signature.no_request, status: EStatusTrackerInfoStatus.ERROR }];

		if (props.productSubscribed?.step === ESubStep.validated || hasValidateSubscription) {
			const validatedAt = props.productSubscribed?.validationDate;
			return [
				{
					text: I18n.asset.component.status_tracker_modal.file_validated,
					status: EStatusTrackerInfoStatus.DONE,
					date: getFormattedDate(validatedAt),
				},
			];
		}

		if (isUserCGP) {
			return [{ text: I18n.asset.component.status_tracker_modal.waiting, status: EStatusTrackerInfoStatus.PENDING }];
		} else {
			return [{ button: { text: I18n.asset.component.status_tracker_modal.validate, onClick: validateSubscription } }];
		}
	}

	const combinedSignature = props.signatureInfo.some((info) => info.type === ESubscriptionSignatureType.subscription && info.includesRto);
	const stepsConfig: IStepsConfig[] = [
		{
			title: combinedSignature ? "Signature de la RTO et des bulletins" : I18n.asset.component.status_tracker_modal.rto_signature,
			position: EStatusTrackerPosition.TOP,
			status: stepStatus.signatureRto,
			info: getSignatureInfo(ESubscriptionSignatureType.rto),
			...(!props.signatureInfo.some((info) => info.sequentialCounterSignature)
				? { resendButton: { text: I18n.asset.component.status_tracker_modal.resend, onClick: toggleResendDocumentsModal } }
				: {}),
		},
		{
			title: I18n.asset.component.status_tracker_modal.subscription_signature,
			position: withoutRto ? EStatusTrackerPosition.TOP : EStatusTrackerPosition.MIDDLE,
			status: stepStatus.signatureSubscription,
			info: getSignatureInfo(ESubscriptionSignatureType.subscription),
			...(!props.signatureInfo.some((info) => info.sequentialCounterSignature)
				? { resendButton: { text: I18n.asset.component.status_tracker_modal.resend, onClick: toggleResendDocumentsModal } }
				: {}),
		},
		{
			title: counterSignature ? I18n.asset.component.status_tracker_modal.counter_signature.transmit_title : I18n.asset.component.status_tracker_modal.send_signed_documents,
			position: EStatusTrackerPosition.MIDDLE,
			status: stepStatus.sendSignedDocuments,
			info: getSendSignedDocumentsInfo(),
		},
		{
			title: counterSignature ? I18n.asset.component.status_tracker_modal.counter_signature.signature_title : I18n.asset.component.status_tracker_modal.validation,
			position: EStatusTrackerPosition.BOTTOM,
			status: stepStatus.validation,
			info: getValidationInfo(),
			...(counterSignature && canResendCounterSignature
				? {
						resendButton: {
							text: counterSignatureRequest?.sequential ? "Renvoyer le lien IQ-EQ" : I18n.asset.component.status_tracker_modal.counter_signature.change_signatory,
							onClick: () => openSignatureAction("transfer"),
							forAllRoles: true,
						},
					}
				: {}),
			...(isCounterSignatureMissing
				? {
						resendButton: {
							text: I18n.asset.component.status_tracker_modal.counter_signature.request_signature,
							onClick: () => openSignatureAction("transfer"),
							forAllRoles: true,
						},
					}
				: {}),
		},
		// Sans convention RTO, l'étape RTO n'existe pas ; signée avec les bulletins, elle tient lieu d'étape de signature du dossier.
	].filter((_, index) => (withoutRto ? index !== 0 : !combinedSignature || index !== 1));

	useEffect(() => {
		setStepStatus(getStepStatus());
	}, [getStepStatus]);

	return (
		<Modal isOpen={props.isOpen} onClose={props.onClose} className={classes["root"]} style={{ maxWidth: "352px" }}>
			<Typography typo={ITypo.H1}>{I18n.asset.component.status_tracker_modal.title}</Typography>

			{props.transferMode?.sequentialAvailable && props.productSubscribed?.step === ESubStep.waiting_signature && props.signatureInfo.length === 0 && (
				<Button onClick={() => openSignatureAction("prepare")}>Préparer les signatures</Button>
			)}
			<div className={classes["steps-container"]}>
				{stepsConfig.map((config, index) => {
					return props.productSubscribed?.step && <StatusTrackerStep key={index} config={config} currentStep={props.productSubscribed.step} isUserCGP={isUserCGP} />;
				})}
			</div>

			<Button variant={EButtonVariant.OUTLINED} onClick={props.onClose}>
				{I18n.asset.component.status_tracker_modal.close}
			</Button>

			<Modal isOpen={isResendDocumentsModalOpen} onClose={toggleResendDocumentsModal} className={classes["resend-modal"]}>
				<Typography typo={ITypo.H1}>{I18n.asset.component.status_tracker_modal.resend_document_modal.title}</Typography>

				<Typography typo={ITypo.P_MEDIUM_BOLD} className={classes["description"]}>
					{I18n.asset.component.status_tracker_modal.resend_document_modal.description}
				</Typography>

				<div className={classes["button-container"]}>
					<Button variant={EButtonVariant.OUTLINED} onClick={toggleResendDocumentsModal}>
						{I18n.asset.component.status_tracker_modal.resend_document_modal.cancel}
					</Button>
					<Button variant={EButtonVariant.CONTAINED} onClick={resendDocuments} loading={isLoading} disabled={isLoading}>
						{!isLoading && I18n.asset.component.status_tracker_modal.resend_document_modal.confirm}
					</Button>
				</div>
			</Modal>

			<TransferDossierModal
				action={signatureAction}
				isOpen={transferDossierModal.isOpen}
				onClose={transferDossierModal.close}
				productSubscribedId={props.productSubscribed?.id ?? null}
				pendingRequest={counterSignatureRequest}
				onTransferred={onDossierTransferred}
			/>

			<Snackbar open={transferredTo !== null} autoHideDuration={6000} onClose={() => setTransferredTo(null)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
				<Alert severity="success" sx={{ width: "100%" }} onClose={() => setTransferredTo(null)}>
					{transferredTo !== null && I18n.trslt(I18n.asset.component.status_tracker_modal.counter_signature.transferred, { name: transferredTo })}
				</Alert>
			</Snackbar>
			<Snackbar open={openSuccessTransferAlert} autoHideDuration={6000} onClose={toggleSuccessTransferAlert} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
				<Alert severity="success" sx={{ width: "100%" }} onClose={toggleSuccessTransferAlert}>
					{I18n.asset.component.status_tracker_modal.resend_document_modal.transfered}
				</Alert>
			</Snackbar>
			<Snackbar open={openSuccessAlert} autoHideDuration={6000} onClose={closeSuccessAlert} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
				<Alert severity="success" sx={{ width: "100%" }} onClose={closeSuccessAlert}>
					{props.transferMode?.sequentialAvailable ? "Le dossier a été mis à jour." : I18n.asset.component.status_tracker_modal.resend_document_modal.success}
				</Alert>
			</Snackbar>
		</Modal>
	);
}
